import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { BrandMark } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useAuthQuery } from '@/hooks/auth';
import { BusinessProvider, useBusinessQuery } from '@/hooks/business';
import { errorMessage } from '@/lib/api';
import { useWorkspace } from '@/lib/workspace';

export function FullPageLoader() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-5 bg-canvas">
      <BrandMark className="size-11 animate-pulse" />
      <Spinner className="size-5 text-ink-faint" />
    </div>
  );
}

function LoadError({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-canvas px-6 text-center">
      <BrandMark className="size-11" />
      <div>
        <h1 className="font-display text-lg font-semibold text-ink">We couldn’t load easiibill</h1>
        <p className="mt-1 max-w-sm text-sm text-ink-muted">{errorMessage(error)}</p>
      </div>
      <Button onClick={retry}>Try again</Button>
    </div>
  );
}

/** Signed-in users only; everyone else is sent to sign in (and returned afterwards). */
export function RequireAuth() {
  const auth = useAuthQuery();
  const location = useLocation();
  if (auth.isPending) return <FullPageLoader />;
  if (auth.isError) return <LoadError error={auth.error} retry={() => auth.refetch()} />;
  if (!auth.data) {
    const next = `${location.pathname}${location.search}`;
    return (
      <Navigate to={`/signin${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />
    );
  }
  return <Outlet />;
}

/** Signed-out pages (sign in, sign up) redirect signed-in users into the app. */
export function GuestOnly() {
  const auth = useAuthQuery();
  const location = useLocation();
  if (auth.isPending) return <FullPageLoader />;
  if (auth.data) {
    const next = new URLSearchParams(location.search).get('next');
    return (
      <Navigate to={next && next.startsWith('/') && !next.startsWith('//') ? next : '/dashboard'} replace />
    );
  }
  return <Outlet />;
}

/** Ensures an active business is selected and loaded for every tenant page. */
export function RequireWorkspace() {
  const auth = useAuthQuery();
  const { businessId, setBusiness } = useWorkspace();
  const businesses = auth.data?.businesses;
  const valid = businessId !== null && Boolean(businesses?.some((b) => b.id === businessId));
  const fallbackId = businesses?.[0]?.id ?? null;

  useEffect(() => {
    if (!valid && fallbackId) setBusiness(fallbackId);
  }, [valid, fallbackId, setBusiness]);

  const business = useBusinessQuery();
  if (!auth.data) return <FullPageLoader />;
  if (!businesses?.length) return <Navigate to="/onboarding" replace />;
  if (!valid || business.isPending) return <FullPageLoader />;
  if (business.isError) return <LoadError error={business.error} retry={() => business.refetch()} />;
  return (
    <BusinessProvider business={business.data}>
      <Outlet />
    </BusinessProvider>
  );
}
